import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import styles from "./Header.module.css";
import { services } from "../../data/services";
import { motion } from "framer-motion";
import socialLinks from "../../rs.json";

const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isContactPage =
        location.pathname.includes("/contact") ||
        location.pathname.includes("/blog");
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false);
    const [isScrollingDown, setIsScrollingDown] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [showServicesDropdown, setShowServicesDropdown] = useState(false);
    const [isDropdownLeaving, setIsDropdownLeaving] = useState(false);
    const [fullTransparentHeader, setFullTransparentHeader] = useState(true);
    const [isServicesOpen, setIsServicesOpen] = useState(false);

    const servicesLinkRef = useRef(null);
    const dropdownRef = useRef(null);
    const hoverBridgeRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY > lastScrollY && currentScrollY > 200) {
                setIsScrollingDown(true);
            } else {
                setIsScrollingDown(false);
            }

            if (currentScrollY > 10) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }

            if (currentScrollY > 200) {
                setFullTransparentHeader(false);
            } else {
                setFullTransparentHeader(true);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [lastScrollY]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (
                !servicesLinkRef.current ||
                !dropdownRef.current ||
                !hoverBridgeRef.current
            )
                return;

            const isOverServicesLink = servicesLinkRef.current.contains(
                e.target
            );
            const isOverDropdown = dropdownRef.current.contains(e.target);
            const isOverBridge = hoverBridgeRef.current.contains(e.target);

            if (
                !isOverServicesLink &&
                !isOverDropdown &&
                !isOverBridge &&
                showServicesDropdown
            ) {
                setIsDropdownLeaving(true);
                setTimeout(() => {
                    setShowServicesDropdown(false);
                    setIsDropdownLeaving(false);
                }, 300);
            }
        };

        document.addEventListener("mouseover", handleMouseMove);
        return () => {
            document.removeEventListener("mouseover", handleMouseMove);
        };
    }, [showServicesDropdown]);

    useEffect(() => {
        if (window.location.hash) {
            const hash = window.location.hash.split("?")[0].substring(1);
            const urlParams = new URLSearchParams(
                window.location.hash.split("?")[1] || ""
            );
            const serviceId = urlParams.get("service");

            setTimeout(() => {
                const section = document.getElementById(hash);
                if (section) {
                    section.scrollIntoView({ behavior: "smooth" });

                    if (hash === "services" && serviceId) {
                        window.dispatchEvent(
                            new CustomEvent("serviceSelected", {
                                detail: {
                                    serviceId: serviceId,
                                },
                            })
                        );
                    }
                }
            }, 500);
        }
    }, [location.pathname]);

    useEffect(() => {
        const scrollY = window.scrollY;

        if (isMobileMenuOpen) {
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = "100%";
            document.body.classList.add("mobileMenuOpen");
        } else {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.classList.remove("mobileMenuOpen");
            window.scrollTo(0, scrollY);
        }

        return () => {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.classList.remove("mobileMenuOpen");
        };
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
        if (isMobileMenuOpen) {
            setIsMobileMenuClosing(true);
            setTimeout(() => {
                setIsMobileMenuOpen(false);
                setIsMobileMenuClosing(false);
            }, 300);
        } else {
            setIsMobileMenuOpen(true);
        }
    };

    const toggleHeaderVisibility = () => {
        setIsScrollingDown(false);
    };

    const showDropdown = () => {
        setIsDropdownLeaving(false);
        setShowServicesDropdown(true);
    };

    const scrollToTop = () => {
        sessionStorage.setItem("portfolioScrollY", 0);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const scrollToSection = (sectionId, e) => {
        e.preventDefault();

        if (isMobileMenuOpen) {
            toggleMobileMenu();
        }

        if (location.pathname !== "/") {
            navigate(`/#${sectionId}`);
            return;
        }

        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: "smooth" });
            window.history.pushState({}, "", `/#${sectionId}`);
        }
    };

    const toggleServicesAccordion = () => {
        setIsServicesOpen(!isServicesOpen);
    };

    return (
        <div
            className={`
      ${isScrollingDown ? styles.scrollActive : ""} 
    `}
        >
            <header className={styles.header}>
                <div className="relative container">
                    {(showServicesDropdown || isDropdownLeaving) && (
                        <div
                            ref={dropdownRef}
                            className={`${styles.dropdownMenu} ${
                                isDropdownLeaving ? styles.dropdownLeaving : ""
                            } absolute left-0 bg-white rounded-lg shadow-lg py-6 z-20 top-[40px] px-4`}
                            style={{
                                margin: "40px auto 0",
                                marginLeft: "2rem",
                                marginRight: "2rem",
                            }}
                        >
                            <div
                                className={`${styles.dropdownContent} grid grid-cols-1 md:grid-cols-3 gap-4`}
                            >
                                {services.map((service) => (
                                    <Link
                                        key={service.id}
                                        to={`/#services?service=${service.id}`}
                                        className={`${styles.serviceItem} block p-4 text-sm text-gray-800 hover:bg-gray-50 flex items-start transition-all duration-200`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (isContactPage) {
                                                navigate(
                                                    `/#services?service=${service.id}`
                                                );
                                            } else {
                                                const serviceSection =
                                                    document.getElementById(
                                                        "services"
                                                    );
                                                if (serviceSection) {
                                                    serviceSection.scrollIntoView(
                                                        {
                                                            behavior: "smooth",
                                                        }
                                                    );
                                                    window.history.pushState(
                                                        {},
                                                        "",
                                                        `/#services?service=${service.id}`
                                                    );
                                                    window.dispatchEvent(
                                                        new CustomEvent(
                                                            "serviceSelected",
                                                            {
                                                                detail: {
                                                                    serviceId:
                                                                        service.id,
                                                                },
                                                            }
                                                        )
                                                    );
                                                }
                                            }
                                            setShowServicesDropdown(false);
                                        }}
                                    >
                                        <div
                                            className={`${styles.serviceIcon} mr-4 ${service.iconBgClass} rounded-lg p-3`}
                                        >
                                            {service.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold mb-1">
                                                {service.shortTitle}
                                            </h3>
                                            <p className="text-gray-600 text-xs">
                                                {service.shortDescription}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <Link
                        to="/"
                        onMouseEnter={() => toggleHeaderVisibility()}
                        onClick={scrollToTop}
                        className="hidden md:block"
                    >
                        <div className={styles.logoBackground}></div>

                        <div className={styles.logoHoverArea}></div>

                        <div className={`${styles.fixedLogo} header-logo`}>
                            <img
                                src="/images/nyloria-logo.svg"
                                width={32}
                                height={32}
                                alt="nyloria Logo"
                                className="object-contain"
                                loading="lazy"
                            />
                        </div>
                    </Link>

                    <div
                        className={`${styles.headerContent} flex justify-between items-center `}
                        style={{
                            backgroundColor: fullTransparentHeader
                                ? "transparent"
                                : "rgb(102 146 193 / 0.05)",
                            backdropFilter: fullTransparentHeader
                                ? "blur(0)"
                                : "blur(15px)",
                            boxShadow: fullTransparentHeader
                                ? "none"
                                : "0 2px 10px rgba(0, 0, 0, 0.1)",
                        }}
                    >
                        <div className="flex items-center">
                            <div className="w-[30px] opacity-0 mr-5 hidden md:block"></div>
                            <Link to="/" onClick={scrollToTop}>
                                <span className="text-xl md:text-2xl font-heading font-bold text-black header-logo-text title">
                                    NYLORIA
                                </span>
                            </Link>
                        </div>

                        <nav className="hidden md:flex items-center space-x-8 ml-auto">
                            <div className="relative">
                                <Link
                                    to={
                                        isContactPage
                                            ? "/#services"
                                            : "#services"
                                    }
                                    ref={servicesLinkRef}
                                    className="text-black hover:text-gradient transition-colors clickable flex items-center relative"
                                    onMouseEnter={showDropdown}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isContactPage) {
                                            navigate("/#services");
                                        } else {
                                            setShowServicesDropdown(
                                                !showServicesDropdown
                                            );
                                            const section =
                                                document.getElementById(
                                                    "services"
                                                );
                                            if (section) {
                                                section.scrollIntoView({
                                                    behavior: "smooth",
                                                });
                                                window.history.pushState(
                                                    {},
                                                    "",
                                                    `/#services`
                                                );
                                            }
                                        }
                                    }}
                                >
                                    Services
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-4 w-4 ml-1 transition-transform ${
                                            showServicesDropdown
                                                ? "rotate-180"
                                                : ""
                                        }`}
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 0 1 1.414 0L10 10.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {(showServicesDropdown ||
                                        isDropdownLeaving) && (
                                        <div
                                            ref={hoverBridgeRef}
                                            className="absolute"
                                            style={{
                                                top: "-10px",
                                                zIndex: 99999999,
                                                left: "-100%",
                                                width: "400%",
                                                height: "110px", // Increased from 100px to 110px
                                            }}
                                        />
                                    )}
                                </Link>
                            </div>
                            <Link
                                to={
                                    isContactPage ? "/#portfolio" : "#portfolio"
                                }
                                className="text-black hover:text-gradient transition-colors clickable"
                                onClick={(e) => {
                                    if (isContactPage) {
                                    } else {
                                        e.preventDefault();
                                        scrollToSection("portfolio", e);
                                    }
                                }}
                            >
                                Portfolio
                            </Link>
                            {/* Add FAQ link to desktop navigation */}
                            {/* <Link
                                to={isContactPage ? "/#faq" : "#faq"}
                                className="text-black hover:text-gradient transition-colors clickable"
                                onClick={(e) => {
                                    if (isContactPage) {
                                        navigate("/#faq");
                                    } else {
                                        e.preventDefault();
                                        scrollToSection("faq", e);
                                    }
                                }}
                            >
                                FAQ
                            </Link> */}
                            <Link
                                to="/blog"
                                className="text-black hover:text-gradient transition-colors clickable"
                            >
                                Blog
                            </Link>
                            <Link to="/contact">
                                <Button variant="primary">
                                    Contactez-nous
                                </Button>
                            </Link>
                        </nav>

                        <button
                            className="md:hidden text-secondary-800 p-2 clickable"
                            onClick={toggleMobileMenu}
                            aria-label="Toggle menu"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={"M4 6h16M4 12h16M4 18h16"}
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {(isMobileMenuOpen || isMobileMenuClosing) && (
                <div
                    className={`fixed inset-0 z-50 bg-white/95 backdrop-blur-lg flex flex-col justify-between items-center pt-12 pb-6 transition-all duration-300 ${
                        styles.mobileFullscreenMenu
                    } ${isMobileMenuClosing ? styles.menuClosing : ""}`}
                    style={{
                        animation: isMobileMenuClosing
                            ? "fadeOut 0.3s ease-in-out"
                            : "fadeIn 0.3s ease-in-out",
                    }}
                >
                    <button
                        className="absolute top-[56px] right-[30px] text-gray-800 p-2 z-50"
                        onClick={toggleMobileMenu}
                        aria-label="Fermer le menu"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    <div className="w-full">
                        <Link
                            to="/"
                            onClick={() => {
                                scrollToTop();
                                toggleMobileMenu();
                            }}
                            className="flex flex-col items-center mb-10"
                        >
                            <img
                                src="/images/nyloria-logo.svg"
                                width={60}
                                height={60}
                                alt="nyloria Logo"
                                className="object-contain mb-2"
                                loading="lazy"
                            />
                            <span className="text-3xl font-heading font-bold title">
                                NYLORIA
                            </span>
                        </Link>

                        <nav className="flex flex-col w-full px-6">
                            <div className="w-full border-b border-gray-200">
                                <button
                                    className={`flex justify-between items-center w-full py-4 px-4 text-2xl font-medium transition-colors rounded-lg ${
                                        isServicesOpen
                                            ? "bg-gradient text-white shadow-md"
                                            : "text-gray-800 hover:bg-gray-50"
                                    }`}
                                    onClick={toggleServicesAccordion}
                                >
                                    <span>Services</span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-5 w-5 transition-transform duration-300 ${
                                            isServicesOpen ? "rotate-180" : ""
                                        }`}
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 0 1 1.414 0L10 10.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>

                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{
                                        height: isServicesOpen ? "auto" : 0,
                                        opacity: isServicesOpen ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="grid grid-cols-2 gap-3 py-4 px-2">
                                        {services.map((service) => (
                                            <Link
                                                key={service.id}
                                                to={`/#services?service=${service.id}`}
                                                className="flex flex-col items-center text-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors shadow-sm"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(
                                                        `/#services?service=${service.id}`
                                                    );
                                                    window.dispatchEvent(
                                                        new CustomEvent(
                                                            "serviceSelected",
                                                            {
                                                                detail: {
                                                                    serviceId:
                                                                        service.id,
                                                                },
                                                            }
                                                        )
                                                    );
                                                    toggleMobileMenu();
                                                }}
                                            >
                                                <div
                                                    className={`text-xl mb-1 text-gradient`}
                                                >
                                                    {service.icon}
                                                </div>
                                                <span className="text-sm font-medium">
                                                    {service.shortTitle}
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </motion.div>
                            </div>

                            {/* Updated array to include FAQ */}
                            {["Portfolio", "Blog"].map((item, index) => (
                                <motion.div
                                    key={item}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + index * 0.1 }}
                                    className="w-full"
                                >
                                    {item === "Blog" ? (
                                        <Link
                                            to="/blog"
                                            className="font-medium text-2xl text-gray-800 hover:bg-gray-50 py-4 px-4 border-b border-gray-200 rounded-lg my-1 flex items-center transition-all"
                                            onClick={toggleMobileMenu}
                                        >
                                            <span className="text-gradient mr-3">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-6 w-6"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                                                    />
                                                </svg>
                                            </span>
                                            {item}
                                        </Link>
                                    ) : item === "FAQ" ? (
                                        <div
                                            className="font-medium text-2xl text-gray-800 hover:bg-gray-50 py-4 px-4 border-b border-gray-200 rounded-lg my-1 flex items-center transition-all cursor-pointer"
                                            onClick={(e) => {
                                                navigate("/#faq");
                                                toggleMobileMenu();
                                            }}
                                        >
                                            <span className="text-gradient mr-3">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-6 w-6"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            </span>
                                            {item}
                                        </div>
                                    ) : (
                                        <div
                                            className="font-medium text-2xl text-gray-800 hover:bg-gray-50 py-4 px-4 border-b border-gray-200 rounded-lg my-1 flex items-center transition-all cursor-pointer"
                                            onClick={(e) => {
                                                navigate("/#portfolio");
                                                toggleMobileMenu();
                                            }}
                                        >
                                            <span className="text-gradient mr-3">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-6 w-6"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                                    />
                                                </svg>
                                            </span>
                                            {item}
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </nav>
                    </div>

                    <div className="w-full px-6 mt-auto pt-4">
                        <Link
                            to="/contact"
                            className="w-full block mb-8"
                            onClick={toggleMobileMenu}
                        >
                            <Button
                                variant="primary"
                                className="w-full py-4 text-lg shadow-lg"
                            >
                                Contactez-nous
                            </Button>
                        </Link>

                        <div className="flex justify-center space-x-6 mb-4">
                            {[
                                {
                                    name: "linkedin",
                                    url: socialLinks.linkedin || "#",
                                },
                                {
                                    name: "twitter",
                                    url: socialLinks.twitter || "#",
                                },
                                {
                                    name: "instagram",
                                    url: socialLinks.instagram || "#",
                                },
                            ]
                                .filter(
                                    (social) => social.url && social.url !== "#"
                                )
                                .map((social) => (
                                    <a
                                        key={social.name}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group bg-gray-800 hover:bg-gradient text-gray-400 hover:text-white p-2 rounded-full transition-all duration-300 flex items-center justify-center w-8 h-8"
                                        aria-label={`Suivez-nous sur ${social.name}`}
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="white"
                                            viewBox="0 0 24 24"
                                        >
                                            {social.name === "linkedin" && (
                                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                            )}
                                            {social.name === "twitter" && (
                                                <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                                            )}
                                            {social.name === "instagram" && (
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            )}
                                        </svg>
                                    </a>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes fadeOut {
                    from {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    to {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                }
            `}</style>

            {isScrollingDown && !isContactPage && (
                <div className={styles.fixedContactButton}>
                    <Link to="/contact">
                        <Button variant="primary" className="shadow-lg">
                            Contactez-nous
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Header;
